import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import {
  BottomSheet,
  type BottomSheetHandle,
  BottomSheetTextInput,
} from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { showErrorToast } from "@-/dev.improve.haniverse.components.ui.failure-toast";
import { getOrpcClient } from "@-/dev.improve.haniverse.lib.orpc";
import { toast } from "@-/com.npmjs.sonner";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { type Ref, useImperativeHandle, useRef, useState } from "react";
import { View } from "@-/com.npmjs.react-native";

const UniwindBottomSheetTextInput = withUniwind(BottomSheetTextInput);

const INPUT_CLASS_NAME =
  "min-h-36 w-full min-w-0 self-stretch rounded-xs border border-input bg-background px-3 py-3 font-sans text-base text-foreground dark:bg-input/30";

export interface MnemonicFeedbackSheetHandle {
  open: () => void;
}

/**
 * Feedback on the mnemonic currently shown for a character — a report that it reads badly, or a
 * better one to use instead. Sibling of the "Add Mnemonic" sheet, which covers the
 * different case of a character that has no mnemonic at all.
 */
export function MnemonicFeedbackSheet({
  currentMnemonic,
  grapheme,
  ref,
}: {
  currentMnemonic: string;
  grapheme: string;
  ref?: Ref<MnemonicFeedbackSheetHandle>;
}) {
  const sheetRef = useRef<BottomSheetHandle>(null);
  const [feedback, setFeedback] = useRouteBodyState("sheet-feedback-draft", "");
  const [inputResetKey, setInputResetKey] = useState(0);
  const mnemonicFeedbackMutation = useMutation({
    mutationFn: async (submittedFeedback: string) =>
      getOrpcClient.mnemonics.feedback({
        currentMnemonic,
        feedback: submittedFeedback,
        grapheme,
      }),
    mutationKey: ["mnemonics", "feedback", grapheme],
  });

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        setFeedback("");
        setInputResetKey((current) => current + 1);
        mnemonicFeedbackMutation.reset();
        sheetRef.current?.open();
      },
    }),
    [mnemonicFeedbackMutation, setFeedback],
  );

  const close = () => {
    sheetRef.current?.close();
  };

  const submit = () => {
    const trimmedFeedback = feedback.trim();
    if (trimmedFeedback.length === 0 || mnemonicFeedbackMutation.isPending) {
      return;
    }

    mnemonicFeedbackMutation.mutate(trimmedFeedback, {
      onError: (error) => {
        showErrorToast("Could not send feedback", error);
      },
      onSuccess: () => {
        close();
        toast.success(`Feedback sent for ${grapheme}.`);
      },
    });
  };

  return (
    <BottomSheet ref={sheetRef} scrollable>
      <View className="flex-col gap-4 pt-2 pb-1">
        <View className="flex-col gap-1">
          <Text className="font-sans text-lg font-semibold text-foreground">
            Give feedback on {grapheme}
          </Text>
          <Text className="font-sans text-sm leading-5 text-muted-foreground">
            Tell us what doesn&apos;t work about this mnemonic, or suggest a better one.
          </Text>
        </View>

        <UniwindBottomSheetTextInput
          accessibilityLabel={`Feedback for ${grapheme}`}
          autoFocus
          className={INPUT_CLASS_NAME}
          defaultValue={feedback}
          key={inputResetKey}
          maxLength={4000}
          multiline
          onChangeText={setFeedback}
          placeholder="Write your feedback…"
          testID="mnemonic-feedback-input"
          textAlignVertical="top"
        />

        <View className="flex-row justify-end gap-2">
          <Button accessibilityLabel="Cancel feedback" onPress={close} variant="outline">
            <Text>Cancel</Text>
          </Button>
          <Button
            accessibilityLabel="Submit feedback"
            disabled={feedback.trim().length === 0 || mnemonicFeedbackMutation.isPending}
            isLoading={mnemonicFeedbackMutation.isPending}
            onPress={submit}
            testID="mnemonic-feedback-submit"
          >
            <Text>Submit</Text>
          </Button>
        </View>
      </View>
    </BottomSheet>
  );
}
